import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faEllipsisVertical, faReply, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dropdown, Flex, Tooltip} from 'antd';
import {useEffect} from 'react';

import {Text} from '../../../../../component/text/text';
import {Locale} from '../../../../../provider/locale/locale';
import {LangKeyType} from '../../../../../provider/locale/translation/type';
import {useSystem} from '../../../../../provider/system/system-hook';

import * as styles from './reviews-bulk-selection-bar.scss';

type MenuItemConfigType = {
    langKey: LangKeyType;
    onClick: VoidFunction;
    icon?: IconDefinition;
    disabled?: boolean;
    disabledTooltipLangKey?: LangKeyType;
};

type PropsType = {
    selectedCount: number;
    isVisible: boolean;

    toggleAllButtonConfig: {
        langKey: LangKeyType;
        onClick: VoidFunction;
    };

    onCancel: VoidFunction;

    submitButtonConfig: MenuItemConfigType & {disabledTooltipLangKey?: LangKeyType};
    secondaryButtonConfig?: MenuItemConfigType;
    thirdButtonConfig?: MenuItemConfigType;
    fourthButtonConfig?: MenuItemConfigType;
};

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
export function ReviewsBulkSelectionBar(props: PropsType): JSX.Element | null {
    const {
        selectedCount,
        isVisible,
        toggleAllButtonConfig,
        onCancel,
        submitButtonConfig,
        secondaryButtonConfig,
        thirdButtonConfig,
        fourthButtonConfig,
    } = props;

    const {screen} = useSystem();

    useEffect(() => {
        if ((screen.isDesktop || screen.isTablet || screen.isMobile) && isVisible) {
            document.body.dataset.tableControlPanelOpen = 'true';
        } else {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        }

        return () => {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        };
    }, [isVisible, screen.isDesktop, screen.isTablet, screen.isMobile]);

    if (!isVisible) {
        return null;
    }

    const isDisabled = selectedCount === 0;

    function renderMenuLabel(langKey: LangKeyType, disabled: boolean, disabledTooltipLangKey?: LangKeyType) {
        const label = <Locale stringKey={langKey} />;

        if (disabled && disabledTooltipLangKey) {
            return (
                <Tooltip title={<Locale stringKey={disabledTooltipLangKey} />}>
                    <span className={styles.ReviewsBulkSelectionBar_menuItemTooltipLabel}>{label}</span>
                </Tooltip>
            );
        }

        return label;
    }

    const secondaryMenuDisabled = secondaryButtonConfig
        ? (secondaryButtonConfig.disabled ?? false) || isDisabled
        : false;
    const fourthMenuDisabled = fourthButtonConfig ? fourthButtonConfig.disabled ?? isDisabled : false;

    const submitButtonDisabled = isDisabled || (submitButtonConfig.disabled ?? false);
    const submitButtonDisabledTooltip: LangKeyType | null =
        submitButtonConfig.disabledTooltipLangKey && !isDisabled && submitButtonConfig.disabled
            ? submitButtonConfig.disabledTooltipLangKey
            : null;

    const submitButton = (
        <Button
            className={styles.ReviewsBulkSelectionBar_submitButton}
            disabled={submitButtonDisabled}
            onClick={() => submitButtonConfig.onClick()}
            type="primary"
        >
            <span className={styles.ReviewsBulkSelectionBar_submitLabel}>
                <Locale stringKey={submitButtonConfig.langKey} />
            </span>
            <span className={styles.ReviewsBulkSelectionBar_submitIcon}>
                <FontAwesomeIcon icon={faReply} />
            </span>
        </Button>
    );

    const menuItems = [
        ...(secondaryButtonConfig
            ? [
                  {
                      key: 'secondary',
                      icon: secondaryButtonConfig.icon ? <FontAwesomeIcon icon={secondaryButtonConfig.icon} /> : null,
                      label: renderMenuLabel(
                          secondaryButtonConfig.langKey,
                          secondaryMenuDisabled,
                          secondaryButtonConfig.disabledTooltipLangKey
                      ),
                      onClick: secondaryButtonConfig.onClick,
                      disabled: secondaryMenuDisabled,
                  },
              ]
            : []),
        ...(thirdButtonConfig
            ? [
                  {
                      key: 'third',
                      icon: thirdButtonConfig.icon ? <FontAwesomeIcon icon={thirdButtonConfig.icon} /> : null,
                      label: <Locale stringKey={thirdButtonConfig.langKey} />,
                      onClick: thirdButtonConfig.onClick,
                      disabled: isDisabled,
                  },
              ]
            : []),
        ...(fourthButtonConfig
            ? [
                  {
                      key: 'fourth',
                      icon: fourthButtonConfig.icon ? <FontAwesomeIcon icon={fourthButtonConfig.icon} /> : null,
                      label: renderMenuLabel(
                          fourthButtonConfig.langKey,
                          fourthMenuDisabled,
                          fourthButtonConfig.disabledTooltipLangKey
                      ),
                      onClick: fourthButtonConfig.onClick,
                      disabled: fourthMenuDisabled,
                  },
              ]
            : []),
    ];

    return (
        <div className={styles.ReviewsBulkSelectionBar}>
            <Flex align="center" gap={12}>
                <span className={styles.ReviewsBulkSelectionBar_countBadge}>
                    <Text bolder inheritColor>
                        {selectedCount}
                    </Text>
                </span>

                <Text className={styles.ReviewsBulkSelectionBar_selectedLabel} stringKey="REVIEWS__SELECTED_LABEL" />
                <Button
                    className={styles.ReviewsBulkSelectionBar_toggleAll}
                    onClick={() => toggleAllButtonConfig.onClick()}
                    size="small"
                    type="link"
                >
                    <Locale stringKey={toggleAllButtonConfig.langKey} />
                </Button>
            </Flex>
            <Flex align="center" gap={8}>
                {submitButtonDisabledTooltip ? (
                    <Tooltip title={<Locale stringKey={submitButtonDisabledTooltip} />}>
                        <span className={styles.ReviewsBulkSelectionBar_submitButtonTooltipWrap}>{submitButton}</span>
                    </Tooltip>
                ) : (
                    submitButton
                )}
                {menuItems.length > 0 && (
                    <Dropdown
                        getPopupContainer={(triggerNode) => triggerNode.parentElement ?? triggerNode}
                        menu={{items: menuItems}}
                        placement="topRight"
                        trigger={['click']}
                    >
                        <Button
                            className={styles.ReviewsBulkSelectionBar_menuButton}
                            icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
                            type="text"
                        />
                    </Dropdown>
                )}
                <Button
                    className={styles.ReviewsBulkSelectionBar_cancelButton}
                    icon={<FontAwesomeIcon icon={faXmark} />}
                    onClick={onCancel}
                    type="text"
                />
            </Flex>
        </div>
    );
}
